/**
 * DateGroupHeader — date section header for Upcoming, Logbook, Trash.
 *
 * **Upcoming day periods** (v1 style):
 *   [large date number]  [weekday name]    [count]
 *   26                   Thursday
 *
 * **Upcoming month/year periods**:
 *   [month label]  [count]
 *   March 5 - 31   3
 *
 * **History periods** (Logbook/Trash — v1 style):
 *   [capitalized label]                  border-bottom
 *   This month
 *   ─────────────────────────────────────
 */

import { memo } from "react"
import { cn } from "@/shared/utils/general/cn"

interface DateGroupHeaderProps {
  /** Display label, e.g. "Thursday", "March 5 - 31", "Idag" */
  label: string
  /** Number of tasks in this group */
  count: number
  /** For day periods: the date number (e.g. "26") */
  dateNumber?: string
  /** Whether this is the first header (no top border) */
  isFirst?: boolean
  /** "upcoming" = big date numbers + border-top; "history" = v1 compact + border-bottom */
  variant?: "upcoming" | "history"
}

export const DateGroupHeader = memo(function DateGroupHeader({
  label,
  count,
  dateNumber,
  isFirst = false,
  variant = "upcoming",
}: DateGroupHeaderProps) {
  const isEmpty = count === 0
  const isDayHeader = !!dateNumber

  // ── History variant (Logbook / Trash) ──────────────────────────────
  if (variant === "history") {
    return (
      <div
        className={cn(
          "date-group-header flex items-baseline border-b border-foreground/16 mb-2.5 px-3 pb-2.5 pt-1",
          isEmpty && "opacity-25",
        )}
      >
        <span
          className={cn(
            "truncate capitalize text-[15px] font-semibold",
            isEmpty ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {label}
        </span>
        {count > 0 && (
          <span className="ml-auto shrink-0 text-[13px] tabular-nums text-muted-foreground/40">
            {count}
          </span>
        )}
      </div>
    )
  }

  // ── Upcoming variant ───────────────────────────────────────────────
  return (
    <div
      className={cn(
        "date-group-header relative flex min-h-[38px] px-3 ",
        isDayHeader ? "items-center" : "items-baseline",
        isEmpty
          ? isDayHeader
            ? "gap-2 py-2.5"
            : "gap-2 py-2"
          : isDayHeader
            ? ""
            : "gap-2 pb-2 pt-6",
        // Non-day headers keep the full-width border-top
        !isFirst && !isDayHeader && "border-t border-foreground/8",
      )}
    >
      {/* Day header: large date number + weekday in right-side bordered box */}
      {isDayHeader ? (
        <>
          <span
            className={cn(
              "shrink-0 text-[40px] font-bold leading-[1.1] tabular-nums mr-2",
              isEmpty ? "text-muted-foreground/25" : "text-foreground",
            )}
          >
            {dateNumber}
          </span>
          <div
            className={cn(
              "flex min-w-0 grow items-baseline gap-2 pt-1 mt-1",
              !isFirst && "border-t border-foreground/16",
            )}
          >
            <span
              className={cn(
                "truncate text-base font-bold capitalize",
                isEmpty ? "text-muted-foreground/25" : "text-foreground",
              )}
            >
              {label}
            </span>
            {count > 0 && (
              <span className="ml-auto shrink-0 text-[13px] tabular-nums text-muted-foreground/40">
                {count}
              </span>
            )}
          </div>
        </>
      ) : (
        /* Month/year header: text label */
        <span
          className={cn(
            "text-[17px] font-bold",
            isEmpty ? "text-muted-foreground/25" : "text-foreground",
          )}
        >
          {label}
        </span>
      )}

      {/* Count for non-day headers */}
      {!isDayHeader && count > 0 && (
        <span className="ml-auto shrink-0 text-[13px] tabular-nums text-muted-foreground/40">
          {count}
        </span>
      )}
    </div>
  )
})
